/** Manages player attack state machine including windup, active, recovery phases, input buffering, and combo chaining. */
import Phaser from 'phaser';
import { Component } from './Component';
import { ConfigLoader } from '../systems/ConfigLoader';
import type { Position } from '../utils/Types';

export type AttackState = 'idle' | 'windup' | 'active' | 'recovery';
export type AttackType = string;

interface AttackTiming {
  windupMs: number;
  activeMs: number;
  recoveryMs: number;
}

function getAttackTiming(type: AttackType): AttackTiming {
  return ConfigLoader.getAttackTiming(type);
}

/** Drives the player's attack state machine: windup → active → recovery, with input buffering and combo chaining. */
export class CombatComponent implements Component {
  state: AttackState = 'idle';
  attackType: AttackType = 'punch';
  comboCount: number = 0;
  facingDirection: Position = { x: 0, y: 1 };

  /** True when the current attack was initiated from stealth (set by skill effect, reset by beginAttack). */
  stealthAttack: boolean = false;
  /** True when the current attack was initiated during a dash or coyote window (set by skill effect, reset by beginAttack). */
  isDashAttack: boolean = false;
  private stateTimer: number = 0;
  private comboTimer: number = 0;
  private events: Phaser.Events.EventEmitter;

  // Input buffer: stores the buffered attack type during non-idle states (null = no buffer)
  private bufferedAttackType: AttackType | null = null;
  private bufferIsDashAttack: boolean = false;
  private bufferFrames: number = 0;

  /** Optional hook to modify recovery duration (e.g. upgrade effects). */
  resolveRecoveryMs?: (attackType: string, baseRecoveryMs: number) => number;

  constructor(events: Phaser.Events.EventEmitter) {
    this.events = events;
  }

  /** True when any attack phase (windup, active, or recovery) is in progress. */
  get isAttacking(): boolean {
    return this.state !== 'idle';
  }

  /** True only during the recovery phase, when a dash-cancel is possible. */
  get isInRecovery(): boolean {
    return this.state === 'recovery';
  }

  /** Begin an attack of the given type, or buffer the input if an attack is already in progress. */
  startAttack(type: AttackType = 'punch', isDashAttack = false): boolean {
    if (this.state === 'idle') {
      this.beginAttack(type, isDashAttack);
      return true;
    }

    // Buffer the input during non-idle states, preserving the requested attack type
    this.bufferedAttackType = type;
    this.bufferIsDashAttack = isDashAttack;
    this.bufferFrames = ConfigLoader.combat.input.bufferWindowFrames;

    return false;
  }

  /** Attempt to cancel recovery early via a dash, returning true if the cancel succeeded. */
  dashCancel(): boolean {
    if (this.state === 'recovery' && ConfigLoader.combat.input.dashCancelsRecovery) {
      this.state = 'idle';
      this.stateTimer = 0;
      return true;
    }
    return false;
  }

  /** Transition to the windup phase for the given attack type and emit the state change event. */
  private beginAttack(type: AttackType, isDashAttack = false): void {
    this.attackType = type;
    this.stealthAttack = false;
    this.isDashAttack = isDashAttack;
    this.state = 'windup';
    this.stateTimer = getAttackTiming(type).windupMs;
    this.events.emit('attack-state-changed', this.state, type, isDashAttack);
  }

  update(delta: number): void {
    // Decrement input buffer
    if (this.bufferFrames > 0) {
      this.bufferFrames--;
      if (this.bufferFrames <= 0) {
        this.bufferedAttackType = null;
      }
    }

    // Combo window decay
    if (this.state === 'idle' && this.comboCount > 0) {
      this.comboTimer -= delta;
      if (this.comboTimer <= 0) {
        this.comboCount = 0;
      }
    }

    if (this.state === 'idle') return;

    this.stateTimer -= delta;
    if (this.stateTimer <= 0) {
      this.advanceState();
    }
  }

  /**
   * Advance the state machine one step (windup→active→recovery→idle).
   * On recovery exit, chains a buffered combo attack if available.
   */
  private advanceState(): void {
    const timing = getAttackTiming(this.attackType);

    switch (this.state) {
      case 'windup':
        this.state = 'active';
        this.stateTimer = timing.activeMs;
        this.events.emit('attack-state-changed', this.state, this.attackType, this.isDashAttack);
        break;

      case 'active': {
        this.state = 'recovery';
        const recoveryMs = this.resolveRecoveryMs
          ? this.resolveRecoveryMs(this.attackType, timing.recoveryMs)
          : timing.recoveryMs;
        this.stateTimer = recoveryMs;
        this.events.emit('attack-state-changed', this.state, this.attackType, this.isDashAttack);
        break;
      }

      case 'recovery': {
        // Check for buffered combo input — chain with the buffered type, not the original
        const maxChain = ConfigLoader.combat.input.attackChainLength;
        if (this.bufferedAttackType !== null && this.comboCount < maxChain - 1) {
          this.comboCount++;
          this.comboTimer = ConfigLoader.combat.input.comboWindowMs;
          const nextType = this.bufferedAttackType;
          const nextIsDash = this.bufferIsDashAttack;
          this.bufferedAttackType = null;
          this.bufferIsDashAttack = false;
          this.beginAttack(nextType, nextIsDash);
        } else {
          this.state = 'idle';
          this.comboTimer = ConfigLoader.combat.input.comboWindowMs;
          this.events.emit('attack-state-changed', this.state, this.attackType, this.isDashAttack);
        }
        break;
      }
    }
  }

  destroy(): void {
    // Nothing to clean up
  }
}
